import type { ExperimentPipeline } from 'types';
import { useState } from 'react';
import { Plus } from 'lucide-react';
import { cn } from 'helpers/cn';
import { Input } from '@/components/ui/input';
import { FOCUS_RING } from '../../ConsolePanel';

type CreatePipelineFormProps = {
  createPipeline: (name: string) => Promise<ExperimentPipeline | null>;
  onCreated: (pipelineId: number) => void;
  className?: string;
};

/**
 * The name field and its button, extracted so the index toolbar and the first-run
 * screen submit through the same code instead of two copies that can drift.
 */
export const CreatePipelineForm = ({ createPipeline, onCreated, className }: CreatePipelineFormProps) => {
  const [newName, setNewName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const create = async () => {
    const name = newName.trim();
    if (!name) return;
    setIsCreating(true);
    const pipeline = await createPipeline(name);
    setIsCreating(false);
    if (pipeline) {
      setNewName('');
      onCreated(pipeline.id);
    }
  };

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <Input
        value={newName}
        placeholder="New pipeline name"
        aria-label="New pipeline name"
        onChange={(ev) => setNewName(ev.target.value)}
        onKeyDown={(ev) => {
          if (ev.key === 'Enter') {
            ev.preventDefault();
            create();
          }
        }}
        // w-full below `sm`: a 288px fixed field plus a "Create pipeline" button is
        // wider than a 375px viewport, and the field is the one that should give.
        className="border-line bg-field h-9 w-full rounded-lg text-body sm:h-8 sm:w-72"
      />
      <button
        type="button"
        disabled={!newName.trim() || isCreating}
        onClick={create}
        className={cn(
          'bg-cta hover:bg-ctahover text-cta-foreground flex h-9 w-full items-center justify-center gap-1.5',
          'rounded-lg px-3 text-meta font-medium transition-colors duration-150 disabled:opacity-40',
          'sm:h-8 sm:w-auto',
          FOCUS_RING,
        )}
      >
        <Plus className="size-3.5" strokeWidth={1.5} />
        {isCreating ? 'Creating…' : 'Create pipeline'}
      </button>
    </div>
  );
};
